import { safeHref } from '../utils/formatters';

export function createUrbanPlanningRenderer(config) {
    var esc = config && typeof config.esc === 'function'
        ? config.esc
        : function(value) {
            return String(value == null ? '' : value)
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#039;');
        };

    function hasValue(value) {
        return value !== null && value !== undefined && value !== '';
    }

    function formatNumber(value) {
        if (!hasValue(value) || isNaN(Number(value))) {
            return '—';
        }
        return Number(value).toLocaleString('pl-PL', {
            maximumFractionDigits: 2
        });
    }

    function formatPercent(value) {
        return hasValue(value) ? formatNumber(value) + '%' : '—';
    }

    function formatMeters(value) {
        return hasValue(value) ? formatNumber(value) + ' m' : '—';
    }

    function formatSquareMeters(value) {
        return hasValue(value) ? formatNumber(value) + ' m²' : '—';
    }

    function statusLabel(status) {
        if (status === 'available') return 'Dane dostępne';
        if (status === 'multi_parcel') return 'Wybrany obiekt znajduje się na kilku działkach';
        if (status === 'unresolved_parcel') return 'Niepowiązana działka';
        if (status === 'missing_imported_act') return 'Brak planu ogólnego gminy w Metroskopie';
        if (status === 'missing_commune_coverage') return 'Brak planu ogólnego dla gminy';
        if (status === 'no_zone_intersection') return 'Bez przecięcia strefy';
        return status || 'Stan nieznany';
    }

    function emptyStateText(context) {
        if (!context) return '';
        if (context.status === 'unresolved_parcel') {
            return 'Nie udało się powiązać wybranego obiektu z działką ewidencyjną.';
        }
        if (context.status === 'missing_imported_act') {
            return 'Metroskop nie ma jeszcze planu ogólnego tej gminy. Źródło: Rejestr Urbanistyczny.';
        }
        if (context.status === 'missing_commune_coverage') {
            return 'W ostatnim imporcie Rejestr urbanistyczny nie zwrócił bieżącego planu ogólnego dla tej gminy.';
        }
        if (context.status === 'no_zone_intersection') {
            return 'Plan ogólny gminy jest zaimportowany, ale geometria działki nie przecina żadnej strefy planistycznej.';
        }
        return '';
    }

    function renderRow(label, value, wrap) {
        if (!hasValue(value)) {
            return '';
        }
        return '<div class="nm-detail-row"><span>' + esc(label) + '</span><span class="' + (wrap ? 'nm-detail-value--wrap' : '') + '">' + esc(value) + '</span></div>';
    }

    // A value with a safe page address (the parcel identifier) opens that page.
    function renderLinkedValue(value, href) {
        var safe = safeHref(href);
        return safe ? '<a class="ms-link" href="' + esc(safe) + '">' + esc(value) + '</a>' : esc(value);
    }

    function renderChip(label, value, modifier, href) {
        if (!hasValue(value)) {
            return '';
        }
        return '<span class="nm-chip ' + (modifier || '') + '"><b>' + esc(label) + '</b> ' + renderLinkedValue(value, href) + '</span>';
    }

    function renderSourceLinks(context) {
        var source = context.source || {};
        var act = context.act || {};
        var detailUrl = source.detailUrl || act.sourceDetailUrl || null;
        var links = [];

        detailUrl = safeHref(detailUrl);
        if (detailUrl) {
            links.push('<a href="' + esc(detailUrl) + '" target="_blank" rel="noopener noreferrer">Rejestr Urbanistyczny</a>');
        }

        if (!links.length) {
            return '';
        }

        return '<div class="nm-urban-planning__links">' + links.join('') + '</div>';
    }

    function renderAct(context) {
        var act = context.act || {};
        var parcel = context.parcel || {};
        var html = '';

        html += '<div class="nm-urban-planning__chips">'
            + renderChip('Stan', statusLabel(context.status), context.status === 'available' ? 'nm-chip--success' : 'nm-chip--info')
            + renderChip('Działka', parcel.idDzialki || parcel.identifier || parcel.number || null, '', parcel.detailUrl)
            + renderChip('Kod jednostki podziału terytorialnego', parcel.tercCode || (act.tercCode || null))
            + '</div>';

        if (act.title) {
            html += '<p class="nm-urban-planning__act">' + esc(act.title) + '</p>';
        }

        html += renderRow('Status aktu', act.status, true);
        html += renderRow('Obowiązuje od', act.validFrom, false);
        html += renderRow('Publikacja', act.publicationDate, false);

        return html;
    }

    function renderSummaryItem(label, value) {
        if (!hasValue(value)) {
            return '';
        }
        return '<div class="nm-urban-planning__summary-item"><span>' + esc(label) + '</span><strong>' + esc(value) + '</strong></div>';
    }

    function renderProfileIcon(icon) {
        var paths = {
            home: '<path d="M3 11.5 12 4l9 7.5"/><path d="M5 10.5V20h14v-9.5"/><path d="M9.5 20v-6h5v6"/>',
            briefcase: '<rect x="3" y="7" width="18" height="13" rx="2"/><path d="M8 7V5.5A1.5 1.5 0 0 1 9.5 4h5A1.5 1.5 0 0 1 16 5.5V7"/><path d="M3 12h18"/>',
            route: '<circle cx="6" cy="6" r="2"/><circle cx="18" cy="18" r="2"/><path d="M8 6h3a3 3 0 0 1 0 6h2a3 3 0 0 1 3 3v1"/>',
            leaf: '<path d="M5 19c8 0 14-6 14-14-8 0-14 6-14 14Z"/><path d="M5 19 19 5"/>',
            sprout: '<path d="M12 20V10"/><path d="M12 10c-5 0-7-3-7-7 5 0 7 3 7 7Z"/><path d="M12 12c5 0 7-3 7-7-5 0-7 3-7 7Z"/>',
            utility: '<path d="M7 3v6"/><path d="M17 3v6"/><path d="M5 9h14"/><path d="M8 9v4a4 4 0 0 0 8 0V9"/><path d="M12 17v4"/>',
            tree: '<path d="M12 22v-7"/><path d="M7 15h10l-3-4h2l-4-6-4 6h2l-3 4Z"/>',
            waves: '<path d="M3 8c2 0 2-1.5 4-1.5S9 8 11 8s2-1.5 4-1.5S17 8 21 8"/><path d="M3 14c2 0 2-1.5 4-1.5S9 14 11 14s2-1.5 4-1.5S17 14 21 14"/><path d="M3 20c2 0 2-1.5 4-1.5S9 20 11 20s2-1.5 4-1.5S17 20 21 20"/>',
            wheat: '<path d="M12 22V4"/><path d="M8 8c0-2 2-4 4-4 0 2-2 4-4 4Z"/><path d="M16 10c0-2-2-4-4-4 0 2 2 4 4 4Z"/><path d="M8 14c0-2 2-4 4-4 0 2-2 4-4 4Z"/><path d="M16 16c0-2-2-4-4-4 0 2 2 4 4 4Z"/>',
            landmark: '<path d="M4 10h16"/><path d="M6 10v8"/><path d="M10 10v8"/><path d="M14 10v8"/><path d="M18 10v8"/><path d="M3 20h18"/><path d="m12 3 8 5H4l8-5Z"/>',
            pickaxe: '<path d="m14 5 5 5"/><path d="M3 21 14 10"/><path d="M11 4c4-1 7 0 9 3l-3 3c-1-2-3-4-6-6Z"/>',
            factory: '<path d="M3 21V9l6 4V9l6 4V5h6v16H3Z"/><path d="M7 17h1"/><path d="M11 17h1"/><path d="M15 17h1"/>',
            circle: '<circle cx="12" cy="12" r="7"/>'
        };
        return '<svg class="nm-urban-planning__profile-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
            + (paths[icon] || paths.circle)
            + '</svg>';
    }

    function renderProfileItems(label, items, fallback) {
        if (!Array.isArray(items) || !items.length) {
            return renderRow(label, fallback, true);
        }

        var chips = items.map(function(item) {
            var itemLabel = item && item.label ? item.label : '';
            if (!itemLabel) {
                return '';
            }
            return '<span class="nm-urban-planning__profile-item">'
                + renderProfileIcon(item.icon || 'circle')
                + '<span>' + esc(itemLabel) + '</span>'
                + '</span>';
        }).filter(Boolean).join('');

        if (!chips) {
            return renderRow(label, fallback, true);
        }

        return '<div class="nm-urban-planning__profile">'
            + '<div class="nm-urban-planning__profile-label">' + esc(label) + '</div>'
            + '<div class="nm-urban-planning__profile-items">' + chips + '</div>'
            + '</div>';
    }

    function zoneDisplayName(zone) {
        if (!zone) {
            return '';
        }
        var symbolLabel = zone.symbolLabel || zone.name || '';
        var designation = zone.designation || zone.symbol || '';
        if (designation && symbolLabel) {
            return designation + ' - ' + symbolLabel;
        }
        return symbolLabel || designation || 'Strefa planistyczna';
    }

    function renderZoneIntro(zones) {
        if (!Array.isArray(zones) || zones.length < 2) {
            return '';
        }
        return '<p class="nm-detail-copy">Działka przecina kilka stref planu ogólnego. Poniżej pokazujemy tylko części stref, które faktycznie nakładają się na działkę.</p>';
    }

    function renderSingleSummary(context) {
        var zones = Array.isArray(context.zones) ? context.zones : [];
        var firstZone = zones.length ? zones[0] : null;
        var params = firstZone && firstZone.parameters ? firstZone.parameters : {};
        var message = emptyStateText(context);
        var html = '<div class="nm-urban-planning__summary">';

        html += renderSummaryItem('Stan', statusLabel(context.status));
        if (firstZone) {
            html += renderSummaryItem('Największa strefa na działce', zoneDisplayName(firstZone));
            html += renderSummaryItem('Położenie działki względem strefy', firstZone.coverageLabel || formatPercent(firstZone.parcelSharePct));
            html += renderSummaryItem('Maksymalna wysokość zabudowy', formatMeters(params.maxBuildingHeight));
        }

        if (message) {
            html += '<p class="nm-detail-copy">' + esc(message) + '</p>';
        }

        html += '</div>';
        return html;
    }

    function renderZone(zone) {
        var params = zone.parameters || {};
        var explanations = zone.parameterExplanations || {};
        var title = zoneDisplayName(zone);
        var html = '<div class="nm-urban-planning__zone">';

        html += '<div class="nm-urban-planning__zone-head">'
            + '<strong>' + esc(title) + '</strong>'
            + (zone.symbol ? '<span>' + esc(zone.symbol) + '</span>' : '')
            + '</div>';
        html += renderRow('Położenie działki względem strefy', zone.coverageLabel || formatPercent(zone.parcelSharePct), true);
        html += renderRow('Powierzchnia działki w tej strefie', zone.intersectionLabel || formatSquareMeters(zone.intersectionAreaM2), true);
        html += renderProfileItems('Profil podstawowy', zone.basicProfileItems, zone.basicProfile);
        html += renderProfileItems('Profil dodatkowy', zone.additionalProfileItems, zone.additionalProfile);
        html += renderRow('Maksymalna intensywność nadziemna', params.maxAbovegroundIntensity, false);
        if (explanations.maxAbovegroundIntensity) {
            html += '<p class="nm-detail-copy nm-urban-planning__parameter-note">' + esc(explanations.maxAbovegroundIntensity) + '</p>';
        }
        html += renderRow('Maksymalny udział powierzchni zabudowy', formatPercent(params.maxBuildingAreaShare), false);
        html += renderRow('Maksymalna wysokość zabudowy', formatMeters(params.maxBuildingHeight), false);
        html += renderRow('Minimalny udział powierzchni biologicznie czynnej', formatPercent(params.minBiologicallyActiveShare), false);
        html += '</div>';

        return html;
    }

    function renderRegulationAreas(areas) {
        if (!Array.isArray(areas) || !areas.length) {
            return '<p class="nm-detail-copy">Brak przecięć z dodatkowymi obszarami planu ogólnego gminy dla tej działki.</p>';
        }

        return '<div class="nm-urban-planning__areas">'
            + areas.map(function(area) {
                var label = area.typeLabel || area.designation || area.name || 'Obszar planu ogólnego gminy';
                return '<div class="nm-urban-planning__area">'
                    + '<span>' + esc(label) + '</span>'
                    + '<strong>' + esc(formatPercent(area.parcelSharePct)) + '</strong>'
                    + '</div>';
            }).join('')
            + '</div>';
    }

    function countLabel(count) {
        count = Number(count || 0);
        if (!count) return 'brak danych';
        if (count === 1) return '1 pozycja';
        var lastTwo = count % 100;
        var last = count % 10;
        if (last >= 2 && last <= 4 && !(lastTwo >= 12 && lastTwo <= 14)) {
            return count + ' pozycje';
        }
        return count + ' pozycji';
    }

    function documentLinks(item) {
        item = item || {};
        return Array.isArray(item.documents)
            ? item.documents.filter(function(doc) { return doc && safeHref(doc.url); })
            : [];
    }

    function renderDocumentLinks(item) {
        var docs = documentLinks(item);
        if (docs.length) {
            return '<div class="nm-urban-planning__links">'
                + docs.map(function(doc) {
                    return '<a href="' + esc(safeHref(doc.url)) + '" target="_blank" rel="noopener noreferrer"'
                        + (doc.title ? ' title="' + esc(doc.title) + '"' : '')
                        + '>' + esc(doc.label || 'Dokument') + '</a>';
                }).join('')
                + '</div>';
        }
        var sourceDetailUrl = safeHref(item && item.sourceDetailUrl);
        if (sourceDetailUrl) {
            return '<div class="nm-urban-planning__links"><a href="' + esc(sourceDetailUrl) + '" target="_blank" rel="noopener noreferrer">Otwórz w Rejestrze</a></div>';
        }
        return '';
    }

    function renderAdditionalAct(item) {
        item = item || {};
        var html = '<article class="nm-urban-planning__extra-item">';
        html += '<div class="nm-urban-planning__extra-item-head">';
        if (item.typeLabel) {
            html += '<span>' + esc(item.typeLabel) + '</span>';
        }
        html += '<strong>' + esc(item.title || item.typeLabel || 'Akt planowania przestrzennego') + '</strong>';
        html += '</div>';
        html += renderRow('Powiązanie z działką', item.coverageLabel, true);
        html += renderRow('Powierzchnia w zasięgu', item.intersectionLabel, true);
        html += renderRow('Status aktu', item.status, true);
        html += renderRow('Obowiązuje od', item.validFrom, false);
        html += renderRow('Publikacja', item.publicationDate, false);
        html += renderDocumentLinks(item);
        html += '</article>';
        return html;
    }

    // A single-act group collapses to a direct link when that act exposes exactly one
    // document (or no documents but a registry detail URL); multi-document acts stay expandable.
    function primaryDirectLink(items) {
        if (items.length !== 1) {
            return null;
        }
        var docs = documentLinks(items[0]);
        if (docs.length === 1) {
            return { url: docs[0].url, label: 'Otwórz dokument' };
        }
        var sourceDetailUrl = safeHref(items[0].sourceDetailUrl);
        if (docs.length === 0 && sourceDetailUrl) {
            return { url: sourceDetailUrl, label: 'Otwórz dokument' };
        }
        return null;
    }

    function renderExtraPanel(group) {
        group = group || {};
        var items = Array.isArray(group.items) ? group.items : [];
        var label = group.buttonLabel || group.title || 'Zobacz dane';

        if (!items.length) {
            return '<div class="nm-urban-planning__extra-panel nm-urban-planning__extra-panel--empty">'
                + '<div class="nm-urban-planning__extra-row"><span>' + esc(label) + '</span><em>' + esc(countLabel(group.count)) + '</em></div>'
                + '</div>';
        }

        var direct = primaryDirectLink(items);
        if (direct) {
            return '<div class="nm-urban-planning__extra-panel">'
                + '<a class="nm-urban-planning__extra-row" href="' + esc(safeHref(direct.url)) + '" target="_blank" rel="noopener noreferrer">'
                + '<span>' + esc(label) + '</span><em>' + esc(direct.label) + '</em>'
                + '</a></div>';
        }

        var html = '<details class="nm-urban-planning__extra-panel">';
        html += '<summary class="nm-urban-planning__extra-row"><span>' + esc(label) + '</span><em><span class="nm-urban-planning__extra-count">' + esc(countLabel(group.count || items.length)) + '</span><span class="nm-urban-planning__extra-indicator" aria-hidden="true"></span></em></summary>';
        html += '<div class="nm-urban-planning__extra-body">';
        if (group.description) {
            html += '<p class="nm-detail-copy">' + esc(group.description) + '</p>';
        }
        html += items.map(renderAdditionalAct).join('');
        html += '</div></details>';
        return html;
    }

    function renderAdditionalPlanning(context) {
        var groups = context && Array.isArray(context.additionalPlanning)
            ? context.additionalPlanning.filter(function(group) {
                return group && Array.isArray(group.items) && group.items.length > 0;
            })
            : [];
        if (!groups.length) {
            return '';
        }

        var panels = groups.map(renderExtraPanel).join('');

        return '<div class="nm-urban-planning__extra">'
            + '<div class="nm-section-label nm-section-label--sub">Dodatkowe dane z Rejestru</div>'
            + '<div class="nm-urban-planning__extra-panels">'
            + panels
            + '</div>'
            + '</div>';
    }

    function renderSingleDetails(context, compact) {
        var zones = Array.isArray(context.zones) ? context.zones : [];
        var message = emptyStateText(context);
        var html = '';

        html += renderAct(context);
        if (message) {
            html += '<p class="nm-detail-copy">' + esc(message) + '</p>';
            html += renderSourceLinks(context);
            return html;
        }

        if (zones.length) {
            html += '<div class="nm-section-label nm-section-label--sub">Strefy planistyczne</div>';
            html += renderZoneIntro(zones);
            html += zones.slice(0, compact ? 2 : 5).map(renderZone).join('');
        }

        html += '<div class="nm-section-label nm-section-label--sub">Dodatkowe obszary</div>';
        html += renderRegulationAreas(context.regulationAreas || []);
        html += renderSourceLinks(context);
        return html;
    }

    function renderSingleContext(context, compact) {
        var details = renderSingleDetails(context, compact);
        var summary = renderSingleSummary(context);
        var message = emptyStateText(context);
        var additionalPlanning = renderAdditionalPlanning(context);

        if (message) {
            return summary + additionalPlanning + renderSourceLinks(context);
        }

        return summary
            + '<details class="nm-urban-planning__details">'
            + '<summary>Pokaż szczegóły planowania</summary>'
            + '<div class="nm-urban-planning__details-body">'
            + additionalPlanning
            + details
            + '</div>'
            + '</details>';
    }

    function renderMultiParcel(context) {
        var parcels = Array.isArray(context.parcels) ? context.parcels : [];
        var resolution = context.resolution || {};
        var parcelCount = resolution.parcelCount || parcels.length;
        var html = '<div class="nm-urban-planning__summary">'
            + renderSummaryItem('Stan', statusLabel(context.status))
            + renderSummaryItem('Liczba powiązanych działek', parcelCount)
            + '<p class="nm-detail-copy">Pełne informacje planistyczne zależą od konkretnej działki, dlatego pokazujemy je osobno po rozwinięciu.</p>'
            + '</div>';

        if (resolution.capped) {
            html += '<p class="nm-detail-copy">Pokazano pierwsze ' + esc(resolution.returnedParcelCount || parcels.length) + ' działki z powiązanego obiektu.</p>';
        }

        html += '<details class="nm-urban-planning__details">'
            + '<summary>Pokaż działki i planowanie</summary>'
            + '<div class="nm-urban-planning__details-body">';

        parcels.forEach(function(parcelContext, index) {
            var parcel = parcelContext.parcel || {};
            var identifier = parcel.idDzialki || parcel.identifier || parcel.number || parcel.id || '—';
            html += '<section class="nm-urban-planning__parcel" aria-label="Działka ewidencyjna ' + esc(identifier) + '">'
                + '<div class="nm-urban-planning__parcel-title"><span>Działka ewidencyjna ' + esc(index + 1) + '</span><strong>' + renderLinkedValue(identifier, parcel.detailUrl) + '</strong></div>'
                + renderSingleContext(parcelContext, true)
                + '</section>';
        });

        html += '</div></details>';
        return html;
    }

    function render(context) {
        if (!context) {
            return '';
        }

        var body = context.status === 'multi_parcel'
            ? renderMultiParcel(context)
            : renderSingleContext(context, false);

        if (!body) {
            return '';
        }

        return '<div class="ms-card ms-card--grid nm-detail-block nm-urban-planning ms-dept--environment">'
            + '<div class="nm-section-label">Planowanie przestrzenne</div>'
            + body
            + '</div>';
    }

    return {
        render: render
    };
}
